export type ContactPointMutationSnapshot = {
  contactPointId: string | null
  kind: string
  value: string
  label: string
  customerId: string | null
  propertyId: string | null
}

export type ContactPointMutationAction = "append" | "replace" | "reject" | "delete"

export type NormalizedContactPointDecision = {
  action: ContactPointMutationAction
  affectedContactPoint: ContactPointMutationSnapshot | null
  replacementContactPoint: ContactPointMutationSnapshot | null
}

export type NormalizedContactPointsOutput = {
  decisions: NormalizedContactPointDecision[]
}

type JsonObject = Record<string, unknown>

const objectValue = (value: unknown): JsonObject | null =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as JsonObject) : null

const textValue = (value: unknown): string => (typeof value === "string" ? value.trim() : "")

const optionalText = (value: unknown): string | null => textValue(value) || null

const snapshotValue = (value: unknown): ContactPointMutationSnapshot | null => {
  const snapshot = objectValue(value)

  if (!snapshot) return null

  const kind = textValue(snapshot.kind).toUpperCase()
  const pointValue = textValue(snapshot.value)

  if (!kind || !pointValue) return null

  return {
    contactPointId: optionalText(snapshot.contact_point_id),
    kind,
    value: pointValue,
    label: textValue(snapshot.label),
    customerId: optionalText(snapshot.customer_id),
    propertyId: optionalText(snapshot.property_id),
  }
}

const actionValue = (value: unknown): ContactPointMutationAction | null => {
  const action = textValue(value).toUpperCase()

  if (action === "ACCEPT_CONTACT_POINT") return "append"

  if (action === "REPLACE_CONTACT_POINT") return "replace"

  if (action === "REJECT_CONTACT_POINT") return "reject"

  if (action === "DELETE_CONTACT_POINT") return "delete"

  return null
}

const decisionValue = (value: unknown): NormalizedContactPointDecision | null => {
  const decision = objectValue(value)

  if (!decision) return null

  const action = actionValue(decision.action)
  const affectedContactPoint = snapshotValue(decision.affected_contact_point_snapshot)

  if (!action) return null

  const targetlessReject =
    action === "reject" &&
    !optionalText(decision.contact_point_id) &&
    !optionalText(decision.kind) &&
    !optionalText(decision.value)

  if (!affectedContactPoint && !targetlessReject) return null

  const replacementContactPoint = snapshotValue(decision.replacement_contact_point_snapshot)

  if (action === "replace" && !replacementContactPoint) return null

  if (action !== "replace" && replacementContactPoint) return null

  return { action, affectedContactPoint, replacementContactPoint }
}

export const normalizeContactPointsOutput = (
  value: unknown,
): NormalizedContactPointsOutput | null => {
  const output = objectValue(value)

  if (!output || !Array.isArray(output.decisions) || !output.decisions.length) {
    return null
  }

  const decisions = output.decisions.map(decisionValue)

  if (decisions.some((decision) => decision === null)) return null

  return { decisions: decisions as NormalizedContactPointDecision[] }
}
